import { useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type { components } from '@/api/generated/schema';

export type AppointmentEmails = components['schemas']['AppointmentEmailsResponse'];
export type AppointmentEmail = components['schemas']['AppointmentEmailResponse'];
export type AppointmentRecipient = components['schemas']['AppointmentRecipientResponse'];
export type AppointmentEmailsChange = components['schemas']['AppointmentEmailsChangeResponse'];
export type VisitOnMyWay = components['schemas']['VisitOnMyWayResponse'];
export type OnMyWaySent = components['schemas']['OnMyWaySentResponse'];

/** The arrival estimates "on my way" offers (`AppointmentEmails.EtaChoices`), in minutes. */
export const ETA_CHOICES = [15, 30, 45, 60] as const;

/**
 * The API declined and said why, in a sentence (M2-21): a 409's body, a 403's
 * ProblemDetails `detail` (the "only the technician on this visit" refusal),
 * or a 400's first field error. Carried up verbatim — the sentence is the part
 * a person can act on, and every negative test asserts it whole.
 */
export class AppointmentRefusedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AppointmentRefusedError';
  }
}

function refusalOrFailure(error: unknown, response: Response, what: string): Error {
  if (response.status === 409 && typeof error === 'string') {
    return new AppointmentRefusedError(error);
  }
  const problem = error as { detail?: string; errors?: Record<string, string[]> } | undefined;
  if (response.status === 403 && typeof problem?.detail === 'string' && problem.detail !== '') {
    return new AppointmentRefusedError(problem.detail);
  }
  const first = problem?.errors ? Object.values(problem.errors).flat()[0] : undefined;
  if (response.status === 400 && first) {
    return new AppointmentRefusedError(first);
  }
  return new Error(`${what} failed: ${response.status}`);
}

/**
 * "On my way" for one visit (M2-21, Kyle's 4A): `POST …/on-my-way` with an
 * optional ETA. The emails go at once.
 *
 * ⚠️ Invalidates the job's own detail: its `onMyWay` (the ten-minute floor
 * now holds) and its `appointmentEmails` (two new rows) both move. The board's
 * card dialog reads the same `['jobs', jobId]` entry, so one key covers both
 * screens.
 */
export function useSendOnMyWay(jobId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ visitId, etaMinutes }: { visitId: string; etaMinutes: number | null }) => {
      const { data, error, response } = await apiClient.POST('/api/v1/jobs/{jobId}/visits/{visitId}/on-my-way', {
        params: { path: { jobId, visitId } },
        body: { etaMinutes },
      });
      if (!response.ok || !data) {
        throw refusalOrFailure(error, response, 'POST /api/v1/jobs/{jobId}/visits/{visitId}/on-my-way');
      }
      return data;
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['jobs', jobId] }),
  });
}

/**
 * The customer's appointment-email switch, from the office (M2-21):
 * `PUT /customers/{id}/appointment-emails`.
 *
 * ⚠️ Every job query is invalidated too, not just the customer's: each job's
 * detail names whether the customer gets appointment emails
 * (`appointmentEmails.customer.enabled`) and whether "on my way" can be sent
 * at all (`onMyWay[].canSend` — "Nobody on this job gets appointment
 * emails…"). A job page cached across this switch would offer a send the API
 * refuses, or refuse one it would take.
 */
export function useSetCustomerAppointmentEmails(customerId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (enabled: boolean) => {
      const { data, error, response } = await apiClient.PUT('/api/v1/customers/{id}/appointment-emails', {
        params: { path: { id: customerId } },
        body: { enabled },
      });
      if (!response.ok || !data) {
        throw refusalOrFailure(error, response, 'PUT /api/v1/customers/{id}/appointment-emails');
      }
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['customers', customerId] });
      void queryClient.invalidateQueries({ queryKey: ['customers', 'search'] });
      void queryClient.invalidateQueries({ queryKey: ['jobs'] });
    },
  });
}

/**
 * The occupant's appointment-email switch, on their property link (M2-21):
 * `PUT /properties/{pid}/parties/{id}/appointment-emails`. Refused with a
 * sentence on any link that is not an occupant/tenant. Invalidates every job
 * query for the same reason as the customer's switch.
 */
export function useSetOccupantAppointmentEmails(propertyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ linkId, enabled }: { linkId: string; enabled: boolean }) => {
      const { data, error, response } = await apiClient.PUT(
        '/api/v1/properties/{propertyId}/parties/{id}/appointment-emails',
        { params: { path: { propertyId, id: linkId } }, body: { enabled } },
      );
      if (!response.ok || !data) {
        throw refusalOrFailure(error, response, 'PUT /api/v1/properties/{propertyId}/parties/{id}/appointment-emails');
      }
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['properties', propertyId] });
      void queryClient.invalidateQueries({ queryKey: ['jobs'] });
    },
  });
}

/**
 * Re-reads the job when the ten-minute floor lifts (`nextAllowedAt`), so a
 * disabled "On my way" comes back on its own rather than staying disabled —
 * with a stale "can be sent again at 9:42" — until something else refetches
 * the page. One timer for the earliest instant; nothing when none is set.
 */
export function useRefreshJobAt(jobId: string, instants: readonly (string | null | undefined)[]) {
  const queryClient = useQueryClient();
  const earliest = instants
    .filter((value): value is string => typeof value === 'string')
    .map((value) => new Date(value).getTime())
    .filter((ms) => !Number.isNaN(ms))
    .sort((a, b) => a - b)[0];

  useEffect(() => {
    if (earliest === undefined) return;
    // A second past the instant, so the API's own "now" is past it too.
    const delay = Math.max(0, earliest - Date.now()) + 1000;
    const timer = setTimeout(() => void queryClient.invalidateQueries({ queryKey: ['jobs', jobId] }), delay);
    return () => clearTimeout(timer);
  }, [earliest, jobId, queryClient]);
}

/**
 * Who an "on my way" would reach, as the office sees it: each recipient that
 * gets appointment emails and has an address, the occupant left out when they
 * share the customer's address (one email goes then — the API's rule). Empty
 * for anyone but the office, who are sent `appointmentEmails: null`.
 */
export function reachableAddresses(emails: AppointmentEmails | null | undefined): string[] {
  if (!emails) return [];
  const recipients = [emails.customer, ...(emails.occupant && !emails.occupant.sameAddressAsCustomer ? [emails.occupant] : [])];
  return recipients.filter((r) => r.enabled && r.address).map((r) => r.address!);
}
